import React, { useState } from 'react';
import styles from './styles.module.scss';
import { JobCriteriaData } from '../job-criteria/types';
import CoreResponsibilitiesSection from '../job-criteria/core-responsibilities';
import TechnicalSkillsSection from '../job-criteria/technical-skills';
import SoftSkillsSection from '../job-criteria/soft-skills';
import WorkMotivatorsSection from '../job-criteria/work-motivators/index';
import WorkplaceQualitiesSection from '../job-criteria/workplace-qualities/index';
import WorkStylesSection from '../job-criteria/work-style';
import PersonalityAlignmentsSection from '../job-criteria/personality-alignment';
import BasicExpectationSection from '../job-criteria/basic-expectations';


interface JobCriteriaProps {
  assessmentId: string | null;
  initialData: JobCriteriaData;
  disabled?: boolean;
}

// Every section object exists in the mapped criteria data even when the payload
// carried nothing for it, so rendering on truthiness alone leaves a bare heading
// with an empty body under it. The talent audit hits this hardest: it sends
// operational competence and nothing else, so work culture used to show up as
// three empty titles. Gate on the section actually having rows to render.
const hasRows = (rows?: unknown[]) => Array.isArray(rows) && rows.length > 0;

const EnhancedJobCriteria: React.FC<JobCriteriaProps> = ({ initialData, assessmentId, disabled }) => {
  const [data, setData] = useState<JobCriteriaData>({
    ...initialData,
  });
  const handleEdit = (section: keyof JobCriteriaData) => {
    // Optional: Log or track edit action
  };

  const handleDelete = (section: keyof JobCriteriaData) => {
    setData({
      ...data,
      [section]: Array.isArray(data[section]) ? { [section]: [] } : null,
    });

  };

  const handleSave = (section: keyof JobCriteriaData, updatedData: any) => {
    setData({
      ...data,
      [section]: updatedData,
    });
  };

  return (
    <div className={styles.jobcriteria}>
      {data.basicExpectation && (
        <BasicExpectationSection
          assessmentId={assessmentId}
          data={data.basicExpectation}
          onEdit={() => handleEdit('basicExpectation')}
          onSave={(updatedData) => handleSave('basicExpectation', updatedData)}
          disabled={disabled}
        />
      )}
      {hasRows(data.coreResponsibilities?.responsibilities) && (
        <CoreResponsibilitiesSection
          assessmentId={assessmentId}
          data={data.coreResponsibilities}
          onEdit={() => handleEdit('coreResponsibilities')}
          onDelete={() => handleDelete('coreResponsibilities')}
          onSave={(updatedData) => handleSave('coreResponsibilities', updatedData)}
          disabled={disabled}
        />
      )}
      {hasRows(data.technicalSkills?.technicalSkills) && (
        <TechnicalSkillsSection
          assessmentId={assessmentId}
          data={data.technicalSkills}
          // Same dimension, different framing: the audit measures how employees
          // already operate rather than what a candidate can claim. Comes from the
          // criteria payload, so it stays correct per-conversation.
          heading={data.technicalSkillsHeading}
          onEdit={() => handleEdit('technicalSkills')}
          onDelete={() => handleDelete('technicalSkills')}
          onSave={(updatedData) => handleSave('technicalSkills', updatedData)}
          disabled={disabled}
        />
      )}
      {hasRows(data.softSkills?.softSkills) && (
        <SoftSkillsSection
          assessmentId={assessmentId}
          data={data.softSkills}
          onEdit={() => handleEdit('softSkills')}
          onDelete={() => handleDelete('softSkills')}
          onSave={(updatedData) => handleSave('softSkills', updatedData)}
          disabled={disabled}
        />
      )}
      {hasRows(data.workMotivators?.workMotivators) && (
        <WorkMotivatorsSection
          assessmentId={assessmentId}
          data={data.workMotivators}
          workplaceQualities={data.workplaceQualities}
          onEdit={() => handleEdit('workMotivators')}
          onDelete={() => handleDelete('workMotivators')}
          onSave={(updatedData) => handleSave('workMotivators', updatedData)}
          disabled={disabled}
        />
      )}
      {(hasRows(data.workplaceQualities?.workplaceQualities) || hasRows(data.workplaceQualities?.qualityOptions)) && (
        <WorkplaceQualitiesSection
          assessmentId={assessmentId}
          data={data.workplaceQualities}
          workMotivators={data.workMotivators}
          onEdit={() => handleEdit('workplaceQualities')}
          onDelete={() => handleDelete('workplaceQualities')}
          onSave={(updatedData) => handleSave('workplaceQualities', updatedData)}
          disabled={disabled}
        />
      )}
      {hasRows(data.workStyles?.workStyles) && (
        <WorkStylesSection
          assessmentId={assessmentId}
          data={data.workStyles}
          onEdit={() => handleEdit('workStyles')}
          onDelete={() => handleDelete('workStyles')}
          onSave={(updatedData) => handleSave('workStyles', updatedData)}
          disabled={disabled}
        />
      )}
      {hasRows(data.personalityAlignments?.personalityAlignments) && (
        <PersonalityAlignmentsSection
          assessmentId={assessmentId}
          data={data.personalityAlignments}
          onEdit={() => handleEdit('personalityAlignments')}
          onDelete={() => handleDelete('personalityAlignments')}
          onSave={(updatedData) => handleSave('personalityAlignments', updatedData)}
          disabled={disabled}
        />
      )}
    </div>
  );
};

export default EnhancedJobCriteria;